import {
    differenceInBusinessDays,
    mustNeverExist,
    BigNumber,
    indexOrDie,
    singleOrDie,
    type Serializable,
} from "@repo/utils";
import type { Threshold, ThresholdTrigger } from "../../types.js";
import type { ConfirmationLine, PurchaseOrderComparison, ScalarComparison, ScheduleLine } from "./comparison/types.js";
import { compareExtraLines, hasDeviation } from "./comparison/helpers.js";
import { calculatePositionSplits } from "../../../server/shared.js";
import type { PositionSplitPart } from "../../../erp/sap/patch/calculatePositionSplits.js";

export function evaluateThreshold<T extends Serializable>(args: {
    threshold: Threshold;
    before: T | null;
    after: T | null;
    equal?: (a: T, b: T) => boolean;
}) {
    if (!args.threshold.active) {
        throw new Error("Deactivated threshold can not be evaluated");
    }
    if (args.before === null) {
        return args.after !== null;
    }

    const after = args.after ?? args.before;

    switch (args.threshold.type) {
        case "anyChange":
            if (BigNumber.isBigNumber(args.before) && BigNumber.isBigNumber(after)) {
                return !args.before.eq(after);
            } else {
                return args.equal ? !args.equal(args.before, after) : args.before !== after;
            }
        case "absoluteDifference": {
            let diff = BigNumber(0);
            if (BigNumber.isBigNumber(args.before) && BigNumber.isBigNumber(after)) {
                diff = after.minus(args.before);
            } else if (typeof args.before === "string" && typeof after === "string") {
                diff = BigNumber(differenceInBusinessDays(args.before, after));
            }

            switch (args.threshold.direction) {
                case "both":
                    return diff.abs().gte(args.threshold.value);
                case "positive":
                    return diff.gte(args.threshold.value);
                case "negative":
                    return diff.lte(-args.threshold.value);
                default:
                    throw mustNeverExist(args.threshold.direction);
            }
        }
        case "absoluteValue": {
            if (!BigNumber.isBigNumber(after)) {
                return false;
            }
            switch (args.threshold.direction) {
                case "both":
                    return after.eq(args.threshold.value);
                case "positive":
                    return after.gte(args.threshold.value);
                case "negative":
                    return after.lte(args.threshold.value);
                default:
                    throw mustNeverExist(args.threshold.direction);
            }
        }
        case "percentage": {
            let diff = BigNumber(0);
            if (BigNumber.isBigNumber(args.before) && BigNumber.isBigNumber(after) && !args.before.isZero()) {
                diff = after.minus(args.before).dividedBy(args.before);
            }
            switch (args.threshold.direction) {
                case "both":
                    return diff.abs().gte(args.threshold.value);
                case "positive":
                    return diff.gte(args.threshold.value);
                case "negative":
                    return diff.lte(-args.threshold.value);
                default:
                    throw mustNeverExist(args.threshold.direction);
            }
        }
        case "alwaysWithin":
            return false;
        default:
            throw mustNeverExist(args.threshold);
    }
}

export function evaluateThresholdsForPurchaseOrderComparison(
    comparison: PurchaseOrderComparison,
    thresholds: Threshold[],
): ThresholdTrigger[] {
    return Array.from(
        (function* (): Generator<ThresholdTrigger> {
            for (const threshold of thresholds.filter(x => x.active)) {
                switch (threshold.field) {
                    case "deliveryTerms":
                        yield evaluate({
                            threshold,
                            key: "deliveryTerms",
                            before: comparison.incoterms1.prevValue,
                            after: comparison.incoterms1.nextValue,
                        });
                        break;
                    case "paymentTerms":
                        yield evaluate({
                            threshold,
                            key: "paymentTerms",
                            before: comparison.paymentTerms.prevValue,
                            after: comparison.paymentTerms.nextValue,
                        });
                        break;
                    case "headerExtra":
                        yield evaluate({
                            threshold,
                            key: "headerExtra",
                            before: comparison.extra.prevValue,
                            after: comparison.extra.nextValue,
                            equal: compareExtraLines,
                        });
                        break;
                    case "itemExtra":
                        for (let i = 0; i < comparison.items.length; i++) {
                            const { datapoints, result } = indexOrDie(comparison.items, i);
                            if (result === "matched") {
                                yield evaluate({
                                    threshold,
                                    key: `items.${i}.price`,
                                    before: datapoints.extra.prevValue,
                                    after: datapoints.extra.nextValue,
                                    equal: compareExtraLines,
                                });
                            }
                        }
                        break;
                    case "unitPrice":
                        for (let i = 0; i < comparison.items.length; i++) {
                            const { datapoints, result } = indexOrDie(comparison.items, i);
                            if (result === "matched") {
                                yield evaluate({
                                    threshold,
                                    key: `items.${i}.price`,
                                    before: datapoints.netUnitPrice.prevValue,
                                    after: datapoints.netUnitPrice.nextValue,
                                });
                            }
                        }
                        break;
                    case "deliveryDate":
                        for (let i = 0; i < comparison.items.length; i++) {
                            const { datapoints, result } = indexOrDie(comparison.items, i);
                            if (result === "matched") {
                                yield* evaluateThresholdOnScheduleSplit(
                                    datapoints.scheduleLines,
                                    datapoints.confirmations,
                                    datapoints.quantity,
                                    threshold,
                                    `items.${i}.scheduleLines`,
                                    prev => {
                                        const scheduleDate =
                                            datapoints.scheduleLines[prev.scheduleIdx]?.deliveryDate ?? null;
                                        return prev.confirmationIdx !== null
                                            ? (datapoints.confirmations.prevValue?.[prev.confirmationIdx]
                                                  ?.deliveryDate ?? scheduleDate)
                                            : scheduleDate;
                                    },
                                    next => {
                                        const scheduleDate =
                                            datapoints.scheduleLines[next.scheduleIdx]?.deliveryDate ?? null;
                                        return next.confirmationIdx !== null
                                            ? (datapoints.confirmations.nextValue?.[next.confirmationIdx]
                                                  ?.deliveryDate ?? scheduleDate)
                                            : scheduleDate;
                                    },
                                );
                            }
                        }
                        break;
                    case "quantity":
                        for (let i = 0; i < comparison.items.length; i++) {
                            const { datapoints, result } = indexOrDie(comparison.items, i);

                            if (result === "matched") {
                                yield evaluate({
                                    threshold,
                                    key: `items.${i}.totalQuantity`,
                                    before: datapoints.quantity.prevValue,
                                    after: datapoints.quantity.nextValue,
                                });
                            }
                        }
                        break;
                    case "scheduleLinesQuantity":
                        for (let i = 0; i < comparison.items.length; i++) {
                            const { datapoints, result } = indexOrDie(comparison.items, i);
                            if (result === "matched") {
                                yield* evaluateThresholdOnScheduleSplit(
                                    datapoints.scheduleLines,
                                    datapoints.confirmations,
                                    datapoints.quantity,
                                    threshold,
                                    `items.${i}.scheduleLines`,
                                    prev => prev.quantity,
                                    next => next.quantity,
                                );
                            }
                        }
                        break;
                    case "orderTotalPrice": {
                        const relatedDatapoints = new Set(
                            (function* () {
                                for (let idx = 0; idx < comparison.items.length; idx++) {
                                    const { datapoints, result } = indexOrDie(comparison.items, idx);
                                    if (result === "matched" && hasDeviation(datapoints.netUnitPrice)) {
                                        yield {
                                            key: `items.${idx}.price`,
                                            before: datapoints.netUnitPrice.prevValue,
                                            after: datapoints.netUnitPrice.nextValue,
                                        };
                                    }
                                }
                            })(),
                        );

                        if (relatedDatapoints.size > 0) {
                            const result = evaluate({
                                threshold,
                                key: "totalPrice",
                                before: comparison.netTotalPrice.prevValue,
                                after: comparison.netTotalPrice.nextValue,
                            });

                            if (result.beyond) {
                                for (const datapoint of relatedDatapoints) {
                                    yield {
                                        ...datapoint,
                                        threshold: result.threshold,
                                        beyond: true,
                                    };
                                }
                            }
                        }
                        break;
                    }
                    default:
                        throw mustNeverExist(threshold);
                }
            }
        })(),
    );
}

function* evaluateThresholdOnScheduleSplit<T extends BigNumber | string>(
    scheduleLines: ScheduleLine[],
    confirmations: ScalarComparison<ConfirmationLine[]>,
    quantity: ScalarComparison<BigNumber>,
    threshold: Threshold,
    key: string,
    getPrev: (part: PositionSplitPart) => T | null,
    getNext: (part: PositionSplitPart) => T | null,
): Generator<ThresholdTrigger> {
    const prevSplit = singleOrDie(
        calculatePositionSplits(
            scheduleLines,
            confirmations.prevValue ?? [],
            quantity.prevValue?.toNumber() ?? 0,
            false,
        ),
    );

    const nextSplit = singleOrDie(
        calculatePositionSplits(
            scheduleLines,
            confirmations.nextValue ?? [],
            quantity.nextValue?.toNumber() ?? 0,
            false,
        ),
    );

    const longest = Math.max(prevSplit.length, nextSplit.length);

    for (let idx = 0; idx < longest; idx++) {
        const prev = prevSplit[idx];
        const next = nextSplit[idx];
        const before = prev ? getPrev(prev) : null;
        const after = next ? getNext(next) : null;

        if (before !== after && (before === null || after === null)) {
            yield {
                threshold,
                key,
                before,
                after,
                beyond: true,
            };
        } else {
            yield evaluate({
                threshold,
                key,
                before,
                after,
            });
        }
    }
}

function evaluate<T extends Serializable>(args: {
    threshold: Threshold;
    key: string;
    before: T | null;
    after: T | null;
    equal?: (a: T, b: T) => boolean;
}): ThresholdTrigger {
    return {
        ...args,
        beyond: evaluateThreshold(args),
    };
}
